import type { CreasePattern } from '@/origami/types';
import { isCut } from '@/origami/types';
import type { DerivedTopology } from '@/origami/topology';
import type { RigidSystem } from '@/origami-math/jacobian';
import { evaluateRigidSystem } from '@/origami-math/jacobian';
import { projectedNewtonStep, projectOntoVariety } from '@/origami-math/rigidFoldability';
import {
  mat3Identity,
  mat3Mul,
  mat3Apply,
  rotationAboutAxis,
  type Mat3,
} from '@/geometry/rotation';
import { sub2, normalize2, dist2 } from '@/geometry/vec2';
import { MAX_FOLD_ANGLE, MAX_SOLVER_ITERATIONS, SOLVER_TOLERANCE, clamp } from '@/constants/numeric';
import type { Vec3 } from '@/geometry/vec3';

/**
 * Rigid-origami solver.
 *
 * State is the vector of fold angles rho (one per foldable interior crease).
 * The constraint C(rho) = 0 is the per-vertex loop closure built in
 * origami-math/jacobian.ts. Given a desired rho_t we take the Gauss-Newton step
 *
 *     d1 = -J^+ C                       (return to the constraint variety)
 *     d2 = (I - J^+ J)(rho_t - rho) s   (move toward the target *inside* it)
 *     rho <- rho + d1 + d2
 *
 * with J^+ the damped right pseudo-inverse J^T (J J^T + lambda I)^-1. Damping
 * (Levenberg-Marquardt) is what keeps the step finite at the bifurcation points
 * that flat-foldable patterns are full of. This is Tachi's projection method
 * ("Simulation of Rigid Origami", Origami^4, 2009).
 *
 * If the residual will not come down, we say so. A rigid fold that does not
 * close its loops is not a fold, and the UI must not draw it as if it were.
 */
export interface RigidOptions {
  damping: number;
  maxIterations: number;
  tolerance: number;
  /** Fraction of the remaining distance to the target taken per solve() call. */
  stepScale: number;
}

export const defaultRigidOptions = (): RigidOptions => ({
  damping: 1e-6,
  maxIterations: MAX_SOLVER_ITERATIONS,
  tolerance: SOLVER_TOLERANCE,
  stepScale: 0.35,
});

export interface RigidDiagnostics {
  converged: boolean;
  iterations: number;
  /** Infinity norm of the loop-closure residual, radians. */
  loopClosureResidual: number;
  /** Max spread of a vertex reconstructed from different faces, paper units. */
  vertexSpread: number;
  /** Max relative edge-length error after reconstruction. Should be ~0. */
  maxEdgeLengthError: number;
  /** Largest gap between the achieved and requested fold angle, radians. */
  maxTargetGap: number;
  message: string | null;
}

export interface RigidReconstruction {
  /** World positions per pattern vertex, xyz triples. */
  positions: Float64Array;
  /** 1 for vertices that a face actually placed; 0 for orphans left flat. */
  placed: Uint8Array;
  /** Per-face rigid transform, for debug drawing. */
  faceRotations: Mat3[];
  faceTranslations: Vec3[];
  /** Faces that the spanning tree could not reach (separate kirigami pieces). */
  unreachedFaces: number[];
  vertexSpread: number;
}

export class RigidSolver {
  readonly pattern: CreasePattern;
  readonly topo: DerivedTopology;
  readonly system: RigidSystem;
  rho: Float64Array;
  private lastDiagnostics: RigidDiagnostics | null = null;

  constructor(pattern: CreasePattern, topo: DerivedTopology, system: RigidSystem) {
    this.pattern = pattern;
    this.topo = topo;
    this.system = system;
    this.rho = new Float64Array(system.variableCount);
  }

  reset(): void {
    this.rho.fill(0);
    this.lastDiagnostics = null;
  }

  get diagnostics(): RigidDiagnostics | null {
    return this.lastDiagnostics;
  }

  /**
   * Advance the fold angles toward `target`, staying on the constraint variety.
   * `locked` marks variables the user has pinned; they are held fixed by
   * re-imposing their value after each Newton step.
   */
  solve(
    target: Float64Array,
    options: RigidOptions = defaultRigidOptions(),
    locked?: Uint8Array,
  ): RigidDiagnostics {
    const n = this.system.variableCount;
    if (n === 0) {
      this.lastDiagnostics = {
        converged: true,
        iterations: 0,
        loopClosureResidual: 0,
        vertexSpread: 0,
        maxEdgeLengthError: 0,
        maxTargetGap: 0,
        message: null,
      };
      return this.lastDiagnostics;
    }

    // Unconstrained creases (no interior vertices at all) go straight there.
    if (this.system.constraintCount === 0) {
      for (let i = 0; i < n; i++) {
        if (locked?.[i]) continue;
        this.rho[i] += (target[i] - this.rho[i]) * options.stepScale;
      }
      const recon = this.reconstruct();
      this.lastDiagnostics = {
        converged: true,
        iterations: 0,
        loopClosureResidual: 0,
        vertexSpread: recon.vertexSpread,
        maxEdgeLengthError: this.edgeLengthError(recon.positions, recon.placed),
        maxTargetGap: maxAbsDiff(this.rho, target),
        message: null,
      };
      return this.lastDiagnostics;
    }

    let iterations = 0;
    let residual = Infinity;
    let stalled = false;
    let previousResidual = Infinity;

    for (; iterations < options.maxIterations; iterations++) {
      const { residual: C, jacobian, maxResidual } = evaluateRigidSystem(
        this.system,
        this.rho,
        true,
      );
      residual = maxResidual;
      if (!Number.isFinite(residual)) {
        this.rho.fill(0);
        this.lastDiagnostics = {
          converged: false,
          iterations,
          loopClosureResidual: Infinity,
          vertexSpread: Infinity,
          maxEdgeLengthError: Infinity,
          maxTargetGap: Infinity,
          message: 'The rigid solver produced a non-finite state and was reset.',
        };
        return this.lastDiagnostics;
      }

      const desired = new Float64Array(n);
      for (let i = 0; i < n; i++) {
        desired[i] = locked?.[i] ? 0 : (target[i] - this.rho[i]) * options.stepScale;
      }

      const step = projectedNewtonStep(jacobian, C, desired, options.damping);
      if (!step) {
        stalled = true;
        break;
      }

      let maxStep = 0;
      for (let i = 0; i < n; i++) {
        if (locked?.[i]) continue;
        const before = this.rho[i];
        this.rho[i] = clamp(before + step[i], -MAX_FOLD_ANGLE, MAX_FOLD_ANGLE);
        maxStep = Math.max(maxStep, Math.abs(this.rho[i] - before));
      }

      if (residual < options.tolerance && maxStep < options.tolerance) break;
      if (
        Math.abs(previousResidual - residual) < 1e-14 &&
        residual > options.tolerance * 100
      ) {
        stalled = true;
        break;
      }
      previousResidual = residual;
      if (maxStep < 1e-14) break;
    }

    // Polish: the tangential term leaves a second-order residual behind, so
    // finish with pure Newton corrections before reporting or reconstructing.
    projectOntoVariety(this.system, this.rho, 12, options.damping * 1e-2, options.tolerance);

    const finalEval = evaluateRigidSystem(this.system, this.rho, false);
    const recon = this.reconstruct();
    const converged = finalEval.maxResidual <= Math.max(options.tolerance, 1e-6);
    let message: string | null = null;
    if (!converged) {
      message = `Rigid fold incompatible: loop-closure residual ${finalEval.maxResidual.toExponential(2)} rad after ${iterations} iterations. The pattern is probably not rigid-foldable at this configuration - try the compliant solver.`;
    } else if (stalled) {
      message = 'The rigid solver stopped early (the step became singular). The configuration shown satisfies the constraints but may not have reached the requested fold amount.';
    }

    this.lastDiagnostics = {
      converged,
      iterations,
      loopClosureResidual: finalEval.maxResidual,
      vertexSpread: recon.vertexSpread,
      maxEdgeLengthError: this.edgeLengthError(recon.positions, recon.placed),
      maxTargetGap: maxAbsDiff(this.rho, target),
      message,
    };
    return this.lastDiagnostics;
  }

  /**
   * Fold the flat pattern into 3D by walking a spanning tree of the face
   * adjacency graph and composing one rotation per crossed crease.
   *
   * Crossing the crease whose directed edge in face A is P1 -> P2 rotates face B
   * about that line by -rho (see src/origami/angles.ts for the sign). Because
   * every facet keeps its own flat coordinates, facets are exactly rigid: any
   * error shows up as a *spread* between the several positions a shared vertex
   * gets from different faces, which is exactly what `vertexSpread` reports.
   */
  reconstruct(): RigidReconstruction {
    const pattern = this.pattern;
    const faces = pattern.facesVertices;
    const nF = faces.length;
    const rotations: Mat3[] = new Array(nF);
    const translations: Vec3[] = new Array(nF);
    const visited = new Uint8Array(nF);

    // Face adjacency across foldable interior edges.
    const adjacency: { face: number; edge: number; via: number }[][] = Array.from(
      { length: nF },
      () => [],
    );
    for (const e of this.topo.interiorEdges) {
      if (isCut(pattern.edgesAssignment[e])) continue;
      const [l, r] = this.topo.edgeFaces[e];
      if (l < 0 || r < 0) continue;
      adjacency[l].push({ face: r, edge: e, via: l });
      adjacency[r].push({ face: l, edge: e, via: r });
    }

    const accum = new Float64Array(pattern.verticesCoords.length * 3);
    const counts = new Float64Array(pattern.verticesCoords.length);
    const samples: Map<number, Vec3[]> = new Map();
    const unreached: number[] = [];

    const placeFace = (f: number): void => {
      const M = rotations[f];
      const t = translations[f];
      for (const v of faces[f]) {
        const p = pattern.verticesCoords[v];
        const w = mat3Apply(M, { x: p.x, y: p.y, z: 0 });
        const world = { x: w.x + t.x, y: w.y + t.y, z: w.z + t.z };
        accum[v * 3] += world.x;
        accum[v * 3 + 1] += world.y;
        accum[v * 3 + 2] += world.z;
        counts[v]++;
        const list = samples.get(v);
        if (list) list.push(world);
        else samples.set(v, [world]);
      }
    };

    for (let root = 0; root < nF; root++) {
      if (visited[root]) continue;
      rotations[root] = mat3Identity();
      translations[root] = { x: 0, y: 0, z: 0 };
      visited[root] = 1;
      placeFace(root);
      const queue = [root];
      while (queue.length) {
        const f = queue.shift() as number;
        for (const link of adjacency[f]) {
          if (visited[link.face]) continue;
          const varIndex = this.system.edgeVariable[link.edge];
          const rho = varIndex >= 0 ? this.rho[varIndex] : 0;

          // Directed edge as wound in face f.
          const dir = directedEdgeInFace(pattern, f, link.edge);
          if (!dir) continue;
          const [p1i, p2i] = dir;
          const P1 = pattern.verticesCoords[p1i];
          const P2 = pattern.verticesCoords[p2i];
          const d2 = normalize2(sub2(P2, P1));
          const axis = { x: d2.x, y: d2.y, z: 0 };
          const Rl = rotationAboutAxis(axis, -rho);

          const Mf = rotations[f];
          const tf = translations[f];
          const Mb = mat3Mul(Mf, Rl);
          const p1v = { x: P1.x, y: P1.y, z: 0 };
          const rotP1 = mat3Apply(Rl, p1v);
          const shift = { x: p1v.x - rotP1.x, y: p1v.y - rotP1.y, z: p1v.z - rotP1.z };
          const shiftWorld = mat3Apply(Mf, shift);
          rotations[link.face] = Mb;
          translations[link.face] = {
            x: tf.x + shiftWorld.x,
            y: tf.y + shiftWorld.y,
            z: tf.z + shiftWorld.z,
          };
          visited[link.face] = 1;
          placeFace(link.face);
          queue.push(link.face);
        }
      }
    }

    for (let f = 0; f < nF; f++) if (!visited[f]) unreached.push(f);

    const positions = new Float64Array(pattern.verticesCoords.length * 3);
    const placed = new Uint8Array(pattern.verticesCoords.length);
    let spread = 0;
    for (let v = 0; v < pattern.verticesCoords.length; v++) {
      if (counts[v] === 0) {
        const p = pattern.verticesCoords[v];
        positions[v * 3] = p.x;
        positions[v * 3 + 1] = p.y;
        positions[v * 3 + 2] = 0;
        continue;
      }
      placed[v] = 1;
      positions[v * 3] = accum[v * 3] / counts[v];
      positions[v * 3 + 1] = accum[v * 3 + 1] / counts[v];
      positions[v * 3 + 2] = accum[v * 3 + 2] / counts[v];
      const list = samples.get(v);
      if (list && list.length > 1) {
        for (const s of list) {
          spread = Math.max(
            spread,
            Math.hypot(
              s.x - positions[v * 3],
              s.y - positions[v * 3 + 1],
              s.z - positions[v * 3 + 2],
            ),
          );
        }
      }
    }

    return {
      positions,
      placed,
      faceRotations: rotations,
      faceTranslations: translations,
      unreachedFaces: unreached,
      vertexSpread: spread,
    };
  }

  /**
   * Worst relative edge-length error. Edges touching a vertex that no face
   * placed (a crease stub hanging outside the paper) are skipped: they carry no
   * geometry, and including them would drown the number that matters.
   */
  private edgeLengthError(positions: Float64Array, placed: Uint8Array): number {
    let worst = 0;
    for (let e = 0; e < this.pattern.edgesVertices.length; e++) {
      const [a, b] = this.pattern.edgesVertices[e];
      if (!placed[a] || !placed[b]) continue;
      const rest = dist2(this.pattern.verticesCoords[a], this.pattern.verticesCoords[b]);
      if (rest <= 0) continue;
      const len = Math.hypot(
        positions[a * 3] - positions[b * 3],
        positions[a * 3 + 1] - positions[b * 3 + 1],
        positions[a * 3 + 2] - positions[b * 3 + 2],
      );
      worst = Math.max(worst, Math.abs(len - rest) / rest);
    }
    return worst;
  }
}

/** The edge (u, v) as it appears in face f's counter-clockwise loop. */
function directedEdgeInFace(
  pattern: CreasePattern,
  face: number,
  edge: number,
): [number, number] | null {
  const [a, b] = pattern.edgesVertices[edge];
  const loop = pattern.facesVertices[face];
  for (let i = 0; i < loop.length; i++) {
    const u = loop[i];
    const v = loop[(i + 1) % loop.length];
    if ((u === a && v === b) || (u === b && v === a)) return [u, v];
  }
  return null;
}

function maxAbsDiff(a: Float64Array, b: Float64Array): number {
  let m = 0;
  for (let i = 0; i < a.length; i++) m = Math.max(m, Math.abs(a[i] - b[i]));
  return m;
}
